/**
 * TB Mukt Bharat - Settings Page
 * Fully functional: real user data + persisted preferences + self-service
 * account management (email/password/phone/deactivate/delete) + data export.
 */
import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Settings as SettingsIcon, User, Lock, Bell, Palette, Mail, Shield,
  X, Loader2, AlertCircle, CheckCircle2, Download, AlertTriangle,
} from 'lucide-react'
import { GovLayout, GovPageTitle, GovCard, GovButton } from '@/components/layout/GovLayout'
import { useAuthStore } from '@/stores/authStore'
import { useTheme } from '@/contexts/ThemeContext'
import i18n from '@/i18n'
import settingsService, { type UserPreferences } from '@/services/settingsService'
import userService from '@/services/userService'

// ================================================================================
// SETTING ITEM
// ================================================================================
interface SettingItemProps {
  label: string
  description: React.ReactNode
  action: React.ReactNode
}
const SettingItem = React.memo(({ label, description, action }: SettingItemProps) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
    <div className="flex-1 pr-4 min-w-0">
      <p className="font-medium text-gray-900">{label}</p>
      <p className="text-sm text-gray-500 truncate">{description}</p>
    </div>
    {action}
  </div>
))

// ================================================================================
// TOGGLE
// ================================================================================
interface ToggleProps {
  enabled: boolean
  onChange: (enabled: boolean) => void
  label?: string
  disabled?: boolean
}
const Toggle = React.memo(({ enabled, onChange, label, disabled }: ToggleProps) => (
  <button
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      enabled ? 'bg-blue-600' : 'bg-gray-300'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    aria-label={label}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        enabled ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
))

// ================================================================================
// MODAL
// ================================================================================
interface ModalProps {
  open: boolean
  title: string
  onClose: () => void
  children: React.ReactNode
  maxWidth?: string
}
const SettingsModal = ({ open, title, onClose, children, maxWidth = 'max-w-md' }: ModalProps) => {
  if (!open) return null
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={`w-full ${maxWidth} bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-800">
          <h3 className="font-bold text-gray-900 dark:text-gray-100">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}

const inputCls =
  'w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500'

type ModalType =
  | 'email' | 'password' | 'phone' | 'campaign' | 'data'
  | 'deactivate' | 'delete' | 'twofa' | null

// ================================================================================
// SETTINGS PAGE
// ================================================================================
const SettingsPage = () => {
  const { user, setUser, setToken, setRefreshToken, logout } = useAuthStore()
  const { theme, setTheme } = useTheme()
  const navigate = useNavigate()

  const [prefs, setPrefs] = useState<UserPreferences | null>(null)
  const [loadingPrefs, setLoadingPrefs] = useState(true)
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [modal, setModal] = useState<ModalType>(null)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)

  // email form
  const [newEmail, setNewEmail] = useState('')
  // password form
  const [curPw, setCurPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  // phone form
  const [phone, setPhone] = useState('')
  // delete confirm
  const [delConfirm, setDelConfirm] = useState('')
  const [delPw, setDelPw] = useState('')

  const showMsg = useCallback((type: 'success' | 'error', text: string) => setMsg({ type, text }), [])

  useEffect(() => {
    if (!msg) return
    const t = setTimeout(() => setMsg(null), 4000)
    return () => clearTimeout(t)
  }, [msg])

  const applyAppearance = useCallback((p: UserPreferences) => {
    document.documentElement.dataset.fontSize = p.fontSize || 'medium'
    document.documentElement.dataset.compact = String(!!p.compactMode)
  }, [])

  // Load preferences once
  useEffect(() => {
    let active = true
    setLoadingPrefs(true)
    settingsService
      .getPreferences()
      .then((p) => {
        if (!active) return
        setPrefs(p)
        applyAppearance(p)
        if (p.language && i18n.language !== p.language && ['en', 'hi', 'mr'].includes(p.language)) {
          i18n.changeLanguage(p.language)
        }
      })
      .catch(() => showMsg('error', 'Could not load your preferences'))
      .finally(() => active && setLoadingPrefs(false))
    return () => {
      active = false
    }
  }, [applyAppearance, showMsg])

  // Persist a single preference (optimistic)
  const savePref = useCallback(
    async (partial: Partial<UserPreferences>, applyApp = false) => {
      if (!prefs) return
      const optimistic = { ...prefs, ...partial }
      setPrefs(optimistic)
      if (applyApp) applyAppearance(optimistic)
      try {
        const updated = await settingsService.updatePreferences(partial)
        setPrefs(updated)
        if (applyApp) applyAppearance(updated)
        showMsg('success', 'Preference saved')
      } catch (e: any) {
        setPrefs(prefs) // revert
        showMsg('error', e?.response?.data?.message || 'Could not save preference')
      }
    },
    [prefs, applyAppearance, showMsg],
  )

  const closeModal = () => {
    setModal(null)
    setModalError(null)
    setSaving(false)
  }

  // ---- Account action handlers ----
  const handleChangeEmail = async () => {
    setModalError(null)
    const email = newEmail.trim().toLowerCase()
    if (!email) return setModalError('Enter a new email')
    setSaving(true)
    try {
      const res = await settingsService.changeEmail(email)
      setToken(res.accessToken)
      setRefreshToken(res.refreshToken)
      setUser({ ...(user as any), email })
      showMsg('success', 'Email updated successfully')
      closeModal()
      setNewEmail('')
    } catch (e: any) {
      setModalError(e?.response?.data?.message || 'Could not update email')
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async () => {
    setModalError(null)
    if (!curPw || !newPw) return setModalError('Fill in all fields')
    if (newPw.length < 6) return setModalError('New password must be at least 6 characters')
    if (newPw !== confirmPw) return setModalError('New passwords do not match')
    setSaving(true)
    try {
      await settingsService.changePassword(curPw, newPw)
      showMsg('success', 'Password updated successfully')
      setCurPw(''); setNewPw(''); setConfirmPw('')
      closeModal()
    } catch (e: any) {
      setModalError(e?.response?.data?.message || 'Could not update password')
    } finally {
      setSaving(false)
    }
  }

  const handleChangePhone = async () => {
    setModalError(null)
    setSaving(true)
    try {
      const updated = await userService.updateProfile({ phone: phone.trim() } as any)
      setUser(updated as any)
      showMsg('success', 'Phone number updated')
      closeModal()
    } catch (e: any) {
      setModalError(e?.response?.data?.message || 'Could not update phone number')
    } finally {
      setSaving(false)
    }
  }

  const handleDownloadData = async () => {
    setModalError(null)
    setSaving(true)
    try {
      await settingsService.downloadMyData()
      showMsg('success', 'Your data has been downloaded')
      closeModal()
    } catch (e: any) {
      showMsg('error', e?.response?.data?.message || 'Could not download data')
    } finally {
      setSaving(false)
    }
  }

  const handleDeactivate = async () => {
    setModalError(null)
    setSaving(true)
    try {
      await settingsService.deactivateAccount()
      logout()
      navigate('/login', { replace: true })
    } catch (e: any) {
      setModalError(e?.response?.data?.message || 'Could not deactivate account')
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setModalError(null)
    if (delConfirm !== 'DELETE') return setModalError('Type DELETE to confirm')
    if (!delPw) return setModalError('Enter your password')
    setSaving(true)
    try {
      await settingsService.deleteAccount(delPw)
      logout()
      navigate('/login', { replace: true })
    } catch (e: any) {
      setModalError(e?.response?.data?.message || 'Could not delete account')
      setSaving(false)
    }
  }

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code)
    savePref({ language: code })
  }

  const openModal = (type: ModalType) => {
    setModalError(null)
    if (type === 'email') setNewEmail(user?.email || '')
    if (type === 'phone') setPhone(user?.phone || '')
    if (type === 'delete') { setDelConfirm(''); setDelPw('') }
    setModal(type)
  }

  return (
    <GovLayout>
      <GovPageTitle title="Settings" subtitle="Manage your account preferences" icon={SettingsIcon} />

      {/* Banner */}
      {msg && (
        <div
          className={`mb-4 flex items-center gap-2 px-4 py-3 rounded-xl border text-sm ${
            msg.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          {msg.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
          )}
          {msg.text}
        </div>
      )}

      <div className="space-y-4">
        {/* Account Settings */}
        <GovCard>
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-blue-600" />
            <h2 className="font-bold text-gray-900">Account Settings</h2>
          </div>
          <div className="space-y-0">
            <SettingItem
              label="Email Address"
              description={user?.email || '—'}
              action={<GovButton variant="secondary" size="sm" onClick={() => openModal('email')}>Change</GovButton>}
            />
            <SettingItem
              label="Password"
              description="Change your password"
              action={<GovButton variant="secondary" size="sm" onClick={() => openModal('password')}>Update</GovButton>}
            />
            <SettingItem
              label="Phone Number"
              description={user?.phone || 'Not set'}
              action={<GovButton variant="secondary" size="sm" onClick={() => openModal('phone')}>Edit</GovButton>}
            />
            <SettingItem
              label="Two-Factor Authentication"
              description="Add extra security to your account"
              action={<GovButton variant="primary" size="sm" onClick={() => openModal('twofa')}>Enable</GovButton>}
            />
          </div>
        </GovCard>

        {/* Privacy Settings */}
        <GovCard>
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-blue-600" />
            <h2 className="font-bold text-gray-900">Privacy Settings</h2>
          </div>
          <div className="space-y-0">
            <SettingItem
              label="Private Account"
              description="Only approved followers can see your posts"
              action={
                <Toggle
                  enabled={!!prefs?.privateAccount}
                  onChange={(v) => savePref({ privateAccount: v })}
                  label="Private Account"
                  disabled={loadingPrefs}
                />
              }
            />
            <SettingItem
              label="Activity Status"
              description="Show when you're active on the platform"
              action={
                <Toggle
                  enabled={!!prefs?.activityStatus}
                  onChange={(v) => savePref({ activityStatus: v })}
                  label="Activity Status"
                  disabled={loadingPrefs}
                />
              }
            />
            <SettingItem
              label="Profile Visibility"
              description="Who can view your profile"
              action={
                <select
                  value={prefs?.profileVisibility || 'public'}
                  onChange={(e) => savePref({ profileVisibility: e.target.value })}
                  disabled={loadingPrefs}
                  className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                >
                  <option value="public">Public</option>
                  <option value="followers">Followers Only</option>
                  <option value="private">Private</option>
                </select>
              }
            />
            <SettingItem
              label="Data Download"
              description="Download a copy of your data"
              action={<GovButton variant="secondary" size="sm" onClick={() => openModal('data')}>Request</GovButton>}
            />
          </div>
        </GovCard>

        {/* Notification Settings */}
        <GovCard>
          <div className="flex items-center gap-2 mb-4">
            <Bell className="w-5 h-5 text-blue-600" />
            <h2 className="font-bold text-gray-900">Notification Settings</h2>
          </div>
          <div className="space-y-0">
            <SettingItem
              label="Push Notifications"
              description="Receive notifications on your device"
              action={
                <Toggle
                  enabled={!!prefs?.pushNotifications}
                  onChange={(v) => savePref({ pushNotifications: v })}
                  label="Push Notifications"
                  disabled={loadingPrefs}
                />
              }
            />
            <SettingItem
              label="Email Notifications"
              description="Receive notifications via email"
              action={
                <Toggle
                  enabled={!!prefs?.emailNotifications}
                  onChange={(v) => savePref({ emailNotifications: v })}
                  label="Email Notifications"
                  disabled={loadingPrefs}
                />
              }
            />
            <SettingItem
              label="Campaign Updates"
              description="Get updates about TB awareness campaigns"
              action={<GovButton variant="secondary" size="sm" onClick={() => openModal('campaign')}>Manage</GovButton>}
            />
            <SettingItem
              label="Reminder Frequency"
              description="How often to receive reminders"
              action={
                <select
                  value={prefs?.reminderFrequency || 'weekly'}
                  onChange={(e) => savePref({ reminderFrequency: e.target.value })}
                  disabled={loadingPrefs}
                  className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              }
            />
          </div>
        </GovCard>

        {/* Appearance Settings */}
        <GovCard>
          <div className="flex items-center gap-2 mb-4">
            <Palette className="w-5 h-5 text-blue-600" />
            <h2 className="font-bold text-gray-900">Appearance Settings</h2>
          </div>
          <div className="space-y-0">
            <SettingItem
              label="Dark Mode"
              description="Use dark theme for better visibility at night"
              action={
                <Toggle
                  enabled={theme === 'dark'}
                  onChange={(v) => {
                    setTheme(v ? 'dark' : 'light')
                    showMsg('success', 'Theme updated')
                  }}
                  label="Dark Mode"
                />
              }
            />
            <SettingItem
              label="Compact Mode"
              description="Show more content with reduced spacing"
              action={
                <Toggle
                  enabled={!!prefs?.compactMode}
                  onChange={(v) => savePref({ compactMode: v }, true)}
                  label="Compact Mode"
                  disabled={loadingPrefs}
                />
              }
            />
            <SettingItem
              label="Language"
              description="Select your preferred language"
              action={
                <select
                  value={prefs?.language || i18n.language || 'en'}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  disabled={loadingPrefs}
                  className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                >
                  <option value="en">English</option>
                  <option value="hi">हिन्दी (Hindi)</option>
                  <option value="mr">मराठी (Marathi)</option>
                </select>
              }
            />
            <SettingItem
              label="Font Size"
              description="Adjust text size for readability"
              action={
                <select
                  value={prefs?.fontSize || 'medium'}
                  onChange={(e) => savePref({ fontSize: e.target.value }, true)}
                  disabled={loadingPrefs}
                  className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              }
            />
          </div>
        </GovCard>

        {/* Danger Zone */}
        <GovCard className="border-red-100 bg-red-50/50">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-red-600" />
            <h2 className="font-bold text-gray-900">Account Actions</h2>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium text-gray-900">Deactivate Account</p>
                <p className="text-sm text-gray-500">Temporarily disable your account</p>
              </div>
              <GovButton
                variant="secondary"
                size="sm"
                className="border-red-200 text-red-600 hover:bg-red-50"
                onClick={() => openModal('deactivate')}
              >
                Deactivate
              </GovButton>
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium text-gray-900">Delete Account</p>
                <p className="text-sm text-gray-500">Permanently delete your account and data</p>
              </div>
              <GovButton
                variant="secondary"
                size="sm"
                className="border-red-200 text-red-600 hover:bg-red-50"
                onClick={() => openModal('delete')}
              >
                Delete
              </GovButton>
            </div>
          </div>
        </GovCard>
      </div>

      {/* ===================== MODALS ===================== */}
      <SettingsModal open={modal === 'email'} title="Change Email Address" onClose={closeModal}>
        <div className="space-y-3">
          <p className="text-sm text-gray-500">
            Your email is used to log in. After changing it, your session will be refreshed.
          </p>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="new@email.com"
            className={inputCls}
          />
          {modalError && <p className="text-sm text-red-600">{modalError}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <GovButton variant="secondary" size="sm" onClick={closeModal}>Cancel</GovButton>
            <GovButton size="sm" onClick={handleChangeEmail} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </GovButton>
          </div>
        </div>
      </SettingsModal>

      <SettingsModal open={modal === 'password'} title="Update Password" onClose={closeModal}>
        <div className="space-y-3">
          <input
            type="password"
            value={curPw}
            onChange={(e) => setCurPw(e.target.value)}
            placeholder="Current password"
            className={inputCls}
          />
          <input
            type="password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            placeholder="New password (min 6 chars)"
            className={inputCls}
          />
          <input
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            placeholder="Confirm new password"
            className={inputCls}
          />
          {modalError && <p className="text-sm text-red-600">{modalError}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <GovButton variant="secondary" size="sm" onClick={closeModal}>Cancel</GovButton>
            <GovButton size="sm" onClick={handleChangePassword} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update'}
            </GovButton>
          </div>
        </div>
      </SettingsModal>

      <SettingsModal open={modal === 'phone'} title="Edit Phone Number" onClose={closeModal}>
        <div className="space-y-3">
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91 98765 43210"
            className={inputCls}
          />
          {modalError && <p className="text-sm text-red-600">{modalError}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <GovButton variant="secondary" size="sm" onClick={closeModal}>Cancel</GovButton>
            <GovButton size="sm" onClick={handleChangePhone} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </GovButton>
          </div>
        </div>
      </SettingsModal>

      <SettingsModal open={modal === 'twofa'} title="Two-Factor Authentication" onClose={closeModal}>
        <div className="space-y-3">
          <div className="flex items-start gap-2 text-sm text-gray-600">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p>
              Two-factor authentication is not available on this deployment yet. It requires
              SMS or email-OTP infrastructure, which is not currently configured on the server.
            </p>
          </div>
          <div className="flex justify-end pt-2">
            <GovButton variant="secondary" size="sm" onClick={closeModal}>Close</GovButton>
          </div>
        </div>
      </SettingsModal>

      <SettingsModal open={modal === 'campaign'} title="Campaign Updates" onClose={closeModal}>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <div>
              <p className="font-medium text-gray-900">Campaign notifications</p>
              <p className="text-sm text-gray-500">Receive updates about TB awareness campaigns</p>
            </div>
            <Toggle
              enabled={!!prefs?.campaignUpdates}
              onChange={(v) => savePref({ campaignUpdates: v })}
              label="Campaign Updates"
              disabled={loadingPrefs}
            />
          </div>
          <div className="flex justify-end pt-1">
            <GovButton variant="secondary" size="sm" onClick={closeModal}>Done</GovButton>
          </div>
        </div>
      </SettingsModal>

      <SettingsModal open={modal === 'data'} title="Download Your Data" onClose={closeModal}>
        <div className="space-y-3">
          <p className="text-sm text-gray-500">
            This downloads a JSON file containing your profile, stats, and your posts.
          </p>
          {modalError && <p className="text-sm text-red-600">{modalError}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <GovButton variant="secondary" size="sm" onClick={closeModal}>Cancel</GovButton>
            <GovButton size="sm" onClick={handleDownloadData} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span className="ml-1">Download</span>
            </GovButton>
          </div>
        </div>
      </SettingsModal>

      <SettingsModal open={modal === 'deactivate'} title="Deactivate Account" onClose={closeModal}>
        <div className="space-y-3">
          <div className="flex items-start gap-2 text-sm text-gray-600">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p>
              Deactivating disables your account — you will be logged out and cannot log back in
              until an administrator reactivates it.
            </p>
          </div>
          {modalError && <p className="text-sm text-red-600">{modalError}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <GovButton variant="secondary" size="sm" onClick={closeModal}>Cancel</GovButton>
            <GovButton
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeactivate}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Deactivate'}
            </GovButton>
          </div>
        </div>
      </SettingsModal>

      <SettingsModal open={modal === 'delete'} title="Delete Account" onClose={closeModal}>
        <div className="space-y-3">
          <div className="flex items-start gap-2 text-sm text-gray-600">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p>
              This permanently deletes your account and all your content. This action cannot be undone.
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-700 mb-1">Type <strong>DELETE</strong> to confirm:</p>
            <input
              value={delConfirm}
              onChange={(e) => setDelConfirm(e.target.value)}
              placeholder="DELETE"
              className={inputCls}
            />
          </div>
          <div>
            <p className="text-sm text-gray-700 mb-1">Enter your password:</p>
            <input
              type="password"
              value={delPw}
              onChange={(e) => setDelPw(e.target.value)}
              placeholder="Password"
              className={inputCls}
            />
          </div>
          {modalError && <p className="text-sm text-red-600">{modalError}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <GovButton variant="secondary" size="sm" onClick={closeModal}>Cancel</GovButton>
            <GovButton
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
              disabled={saving || delConfirm !== 'DELETE'}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete Permanently'}
            </GovButton>
          </div>
        </div>
      </SettingsModal>
    </GovLayout>
  )
}

export default React.memo(SettingsPage)